import { useState, useEffect } from 'react';
import { X, Share, Plus, MoreVertical, Download, Smartphone, Monitor, Tablet } from 'lucide-react';
import { Button } from '../components/ui/button';

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;

  if (/iPad|Macintosh/.test(ua) && 'ontouchend' in document) {
    return 'ipad';
  }
  if (/iPhone|iPod/.test(ua)) {
    return 'ios';
  }
  if (/Android/.test(ua)) {
    if (/Mobile/.test(ua)) {
      return 'android';
    }
    return 'android-tablet';
  }
  return 'desktop';
};

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Check if user previously dismissed
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    setDeviceType(getDeviceType());

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 3 seconds if not recently dismissed
      if (daysSinceDismissed > 3) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
    } catch (err) {
      console.error('Install prompt error:', err);
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showPrompt || isInstalled) return null;

  // Device-specific instructions for iOS/manual install
  const getManualInstructions = () => {
    switch (deviceType) {
      case 'ios':
      case 'ipad':
        return {
          icon: deviceType === 'ipad' ? Tablet : Smartphone,
          title: deviceType === 'ipad' ? 'Add to iPad Home Screen' : 'Add to iPhone Home Screen',
          steps: [
            { icon: Share, text: 'Tap the Share button in Safari' },
            { icon: Plus, text: 'Scroll and tap "Add to Home Screen"' },
            { icon: null, text: 'Tap "Add" to confirm' }
          ],
          note: 'Use Safari browser for best experience'
        };
      default:
        return null;
    }
  };

  const manualInstructions = getManualInstructions();

  // If we have the deferred prompt (Chrome/Edge/etc), show install banner
  if (deferredPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[90] p-3 sm:p-4 animate-slide-up" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}>
        <div className="max-w-md mx-auto bg-card/95 backdrop-blur-xl border rounded-xl shadow-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-primary rounded-xl shadow-lg shrink-0">
              <Smartphone className="h-6 w-6 text-primary-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-foreground font-bold text-base mb-1">
                Install Ahmad Quran
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                Add to your home screen for quick access, offline support & app-like experience.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={handleInstall}
                  size="sm"
                  className="gap-1.5"
                  data-testid="pwa-install-btn"
                >
                  <Download className="h-4 w-4" />
                  Install
                </Button>
                <Button
                  onClick={handleDismiss}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                >
                  Not now
                </Button>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-muted-foreground hover:text-foreground shrink-0 p-1"
              data-testid="pwa-close-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // For iOS (no beforeinstallprompt support), show manual instructions
  if (manualInstructions) {
    const DeviceIcon = manualInstructions.icon;
    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div
          className="relative w-full max-w-md bg-card rounded-2xl shadow-2xl border overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
          data-testid="pwa-install-prompt"
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                <DeviceIcon className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{manualInstructions.title}</h2>
                <p className="text-sm opacity-90 mt-1">Get the full app experience</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="mb-6 p-4 bg-muted/50 rounded-xl">
              <p className="text-sm font-medium mb-2">Why install?</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>{'\u2713'} Quick access from home screen</li>
                <li>{'\u2713'} Full screen experience</li>
                <li>{'\u2713'} Faster loading</li>
              </ul>
            </div>

            <div className="space-y-3 mb-6">
              <p className="text-sm font-medium">How to install:</p>
              {manualInstructions.steps.map((step, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    {step.icon ? (
                      <step.icon className="w-4 h-4 text-primary" />
                    ) : (
                      <span className="text-sm font-medium text-primary">{index + 1}</span>
                    )}
                  </div>
                  <p className="text-sm">{step.text}</p>
                </div>
              ))}
            </div>

            {manualInstructions.note && (
              <p className="text-xs text-muted-foreground mb-4 text-center">
                {manualInstructions.note}
              </p>
            )}

            <Button variant="outline" onClick={handleDismiss} className="w-full">
              Got it
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
